'use client'

import EmptyFlightScreenWrapper from '@/src/components/common/srp/emptyFlightScreen'
import SrpErrorStateWrapper from '@/src/components/common/srp/errorState'
import MulticityRoundTripDialog from '@/src/components/desktop/srp/multicityRoundTripDialog'

import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import AirspaceAlertWrapper from '@/src/components/common/home/common/airspaceAlert'
import LoaderImage from '@/src/components/common/loaderImage'
import FiltersWrapper from '@/src/components/common/srp/filters'
import AirlineFilterWrapperTopBar from '@/src/components/common/srp/filters/airlineFilterTopBar'
import RtSplitFilters from '@/src/components/common/srp/filters/rtSplitFilters'
import NearbyAirportPopup from '@/src/components/common/srp/nearbyAirportPopup'
import StillAroundPopup from '@/src/components/common/srp/stillAroundPopup/StillAroundPopup'
import Statusbar from '@/src/components/common/ui/statusbar'
import CouponSectionWrapper from '@/src/components/desktop/srp/couponSection'
import FareFamilyWrapper from '@/src/components/desktop/srp/fareFamily'
import FlightDetailsWrapperDesktop from '@/src/components/desktop/srp/flightResults/flightDetails'
import FlightDetailsSlider from '@/src/components/desktop/srp/flightResults/flightDetails/flightDetails'
import DesktopFlightCardShimmer from '@/src/components/desktop/srp/loadingShimmers/DesktopFlightCardShimmer'
import DesktopSrpLoadingShimmer from '@/src/components/desktop/srp/loadingShimmers/DesktopSrpLoadingShimmer'
import SearchHeader from '@/src/components/desktop/srp/searchHeader'
import SortTabsDesktop from '@/src/components/desktop/srp/sortTabs'
import { DESKTOP } from '@/src/constants/deviceTypes'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { useAIFeatureEnableManager } from '@/src/hooks/AIFeatureEnableManager'
import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import useNearByAirportAlertManager from '@/src/hooks/srp/nearbyAirportAlertManager'
import useViewportFillHeight from '@/src/hooks/srp/RTSplitView/useViewportFillHeight'
import useSrpController from '@/src/hooks/srp/srpController'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { selectSRPFloatingAIChatEnabled } from '@/src/redux/slices/aiFeatureSlice'
import { useAppSelector } from '@/src/redux/store'
import { useSetLanguageCookie } from '@/src/utils/language'
import { Backdrop, Box, Container, Dialog } from '@mui/material'
import { useLocale } from 'next-intl'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import StickyBox from 'react-sticky-box'

interface SrpContainerDesktopProps {
    domainCountry?: string
}

// Lazy-load the AI chat — heavy dependency tree, and only renders behind a feature flag.
const SRPFloatingAIChat = dynamic(() => import('@/src/components/common/srp/aiFilter/SRPFloatingAIChat'), {
    ssr: false
})

// Shown only if a view's JS chunk is still downloading when results arrive. Usually
// never visible — the chunk fetch overlaps the in-flight search API call.
const ViewChunkFallback = () => (
    <Box sx={{ mt: 2 }}>
        {[...Array(7)].map((_, i) => (
            <DesktopFlightCardShimmer key={i} />
        ))}
    </Box>
)

// Exactly one SRP view renders per search (the mode is known synchronously from the URL),
// so code-split each view root: a one-way search no longer ships the RT-solution/RT-split
// trees, and vice versa. Keeps SSR on — during SSR results are still loading, so the
// shimmer (not these views) is what renders.
const RTSolutionFlowWrapper = dynamic(() => import('@/src/components/desktop/srp/rtSolutionViewFlow'), {
    loading: ViewChunkFallback
})
const RTSplitViewWrapper = dynamic(() => import('@/src/components/desktop/srp/rtSplitView'), {
    loading: ViewChunkFallback
})
const FlightResultsWrapper = dynamic(() => import('@/src/components/desktop/srp/flightResults'), {
    loading: ViewChunkFallback
})

const SrpContainerDesktop = ({ domainCountry }: SrpContainerDesktopProps = {}) => {
    // Set selectedLanguage cookie on page load based on locale
    useSetLanguageCookie()
    // useSmartSearchTour()
    // useSmartSortTour()

    const {
        selectedFlightForFareFamily,
        selectedFlightForFlightDetails,
        handleCreateBooking,
        handleFlightDetailsClose,
        onViewFlightDetails,
        setSelectedFlightForFareFamily,
        handleBookClick,
        rawFareFamilyData,
        setRawFareFamilyData,
        isBookingInProgress,
        loadingFlightKey,
        showStillAroundPopup,
        handleReFetchWithSameQuery,
        handleCloseFareFamily,
    } = useSrpController({
        deviceType: 'DESKTOP'
    })

    const {
        srpResultsLoading,
        srpResultsError,
        invalidUrl,
        showEmptyFlightScreen,
        jsons,
        sectors,
        sid,
        isRT,
        srpViewMode,
        selectedJourneyLeg
    } = useSearchResults()
    const { srpSmartFilterSort } = useAIFeatureEnableManager()
    const srpFloatingAIChatEnabled = useAppSelector(selectSRPFloatingAIChatEnabled)
    // Nearby airport alert manager - handles all nearby airport logic internally
    const {
        handleFlightSelection,
        handleNearbyPopupContinue,
        handleNearbyPopupClose,
        isOpen,
        popupData
    } = useNearByAirportAlertManager({
        onContinue: handleBookClick,
        jsons: jsons!!,
        sectors: sectors!!,
        handleFlightDetailsClose
    })

    const { triggerSrpEvent } = useClevertapForSrp()
    const [menuOpen, setMenuOpen] = useState(false)
    const [roundTripDialogOpen, setRoundTripDialogOpen] = useState(false)
    const closeSearchModifiersRef = useRef<() => void>(() => { })
    const openMulticityEditorRef = useRef<() => void>(() => { })
    // Split view only: makes the filters sidebar an independently-scrollable panel (viewport-tall)
    // instead of a tall sticky column that pushes the page and leaves white space when scrolled.
    // The combined selection card is a viewport-fixed footer (rendered inside RTSplitViewWrapper);
    // reserve its fixed height at the bottom so the sidebar stops at the card's top instead of
    // hiding its last filters behind it.
    const { ref: filtersScrollRef, height: filtersScrollHeight } = useViewportFillHeight<HTMLDivElement>(8)
    // Round-trip-like multicity (J1 A->B, J2 B->A): show the conversion dialog and track it once
    // per detection via Air_UI_Action (NewRelic + CleverTap). Keyed on invalidUrl only.
    useEffect(() => {
        const isRoundTripLikeMulticity = invalidUrl === 'roundTripLikeMulticity'
        setRoundTripDialogOpen(isRoundTripLikeMulticity)
        if (isRoundTripLikeMulticity) {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'round_trip_like_multicity',
                action_type: 'VALIDATION'
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invalidUrl])

    const handleOverlayOpen = () => setMenuOpen(true)
    const handleOverlayClose = () => setMenuOpen(false)
    const handleOverlayBackdropClick = () => {
        closeSearchModifiersRef.current()
        setMenuOpen(false)
    }
    const locale = useLocale()
    useEffect(() => {
        if (srpResultsError) {
            triggerSrpEvent('Air_Failures', {
                action_name: 'srp_search_failure',
                action_type: 'API',
                action_value: `Error ${srpResultsError.status}: 'Failed to fetch search results'`
            })
        }
        if (invalidUrl) {
            triggerSrpEvent('Air_Failures', {
                action_name: invalidUrl === 'dateInPast' ? 'past_date_error' : 'invalid_url',
                action_type: 'VALIDATION',
                action_value: 'Selected date is in the past'
            })
        }
    }, [srpResultsError, invalidUrl, triggerSrpEvent])

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', position: 'relative' }}>
            <LoaderImage height={50} width={600} />
            <Toaster position="bottom-right" reverseOrder={false} gutter={8} />
            <ErrorBoundaryWrapper identifier="HeaderContainer-desktop-srp">
                <HeaderContainer domainCountry={domainCountry} />
            </ErrorBoundaryWrapper>
            <ErrorBoundaryWrapper identifier="Statusbar-desktop-srp">
                <Statusbar loading={srpResultsLoading} />
            </ErrorBoundaryWrapper>
            <Backdrop
                open={menuOpen}
                onClick={handleOverlayBackdropClick}
                sx={{
                    zIndex: 1300,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }}
            />
            <AirspaceAlertWrapper />
            <Container maxWidth="xl" sx={{
                px: { xs: 2, sm: 3, lg: 4 },
                pt: 1,
            }}>
                {invalidUrl !== 'dateInPast' && (
                    <Box
                        sx={theme => ({
                            width: {
                                xs: `calc(100% + ${theme.spacing(3)})`,
                                lg: `calc(100% + ${theme.spacing(4)})`
                            },
                            position: 'relative',
                            zIndex: menuOpen ? 1400 : 1,
                            pb: 1
                        })}
                    >
                        <ErrorBoundaryWrapper identifier="searchHeader-desktop-srp">
                            <SearchHeader
                                jsons={jsons}
                                overlayOpen={handleOverlayOpen}
                                overlayClose={handleOverlayClose}
                                onRegisterCloseAll={closeAll => {
                                    closeSearchModifiersRef.current = closeAll
                                }}
                                onRegisterOpenMulticityEditor={fn => {
                                    openMulticityEditorRef.current = fn
                                }}
                                srpResultsLoading={srpResultsLoading}
                            />
                        </ErrorBoundaryWrapper>
                    </Box>
                )}

                {!!srpResultsError || (invalidUrl && invalidUrl !== 'roundTripLikeMulticity') ? (
                    <ErrorBoundaryWrapper identifier="SrpErrorStateWrapper-desktop-srp">
                        <SrpErrorStateWrapper
                            pastDateError={invalidUrl === 'dateInPast'}
                            errorStatus={invalidUrl === 'requiredFieldsMissing' ? 400 : srpResultsError?.status || 500}
                        />
                    </ErrorBoundaryWrapper>
                ) : invalidUrl === 'roundTripLikeMulticity' ? null : srpResultsLoading ? (
                    <DesktopSrpLoadingShimmer />
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '280px 1fr',
                                gap: { xs: 3, lg: 4 },
                                alignItems: 'start'
                            }}
                        >
                            {srpViewMode === 'rtSplit' ? (
                                <Box
                                    ref={filtersScrollRef}
                                    sx={{
                                        height: filtersScrollHeight,
                                        minHeight: 0,
                                        overflowY: 'auto',
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        scrollbarWidth: 'none'
                                    }}
                                >
                                    <ErrorBoundaryWrapper identifier="sideBar-filters-wrapper-desktop-srp">
                                        <RtSplitFilters deviceType={DESKTOP} />
                                    </ErrorBoundaryWrapper>
                                </Box>
                            ) : (
                                <StickyBox offsetTop={24} offsetBottom={24}>
                                    <ErrorBoundaryWrapper identifier="sideBar-filters-wrapper-desktop-srp">
                                        <FiltersWrapper deviceType={DESKTOP} />
                                    </ErrorBoundaryWrapper>
                                </StickyBox>
                            )}

                            <Box sx={{ order: { xs: 1, lg: 2 } }}>
                                <CouponSectionWrapper jsons={jsons} isRT={isRT} />

                                <>
                                    {srpViewMode === 'rtSolution' ? (
                                        <ErrorBoundaryWrapper identifier="RTSolutionFlowWrapper-desktop-srp">
                                            <RTSolutionFlowWrapper handleFlightBook={handleCreateBooking} />
                                        </ErrorBoundaryWrapper>
                                    ) : srpViewMode === 'rtSplit' ? (
                                        <ErrorBoundaryWrapper identifier="RTSplitViewWrapper-desktop-srp">
                                            <RTSplitViewWrapper
                                                handleFlightBook={handleCreateBooking}
                                                isBookingInProgress={isBookingInProgress}
                                            />
                                        </ErrorBoundaryWrapper>
                                    ) : (
                                        <>
                                            {showEmptyFlightScreen ? (
                                                <ErrorBoundaryWrapper identifier="EmptyFlightScreenWrapper-desktop-srp">
                                                    <EmptyFlightScreenWrapper deviceType={DESKTOP} />
                                                </ErrorBoundaryWrapper>
                                            ) : (
                                                <>
                                                    <ErrorBoundaryWrapper identifier="SortTabWrapper-desktop-srp">
                                                        <SortTabsDesktop />
                                                    </ErrorBoundaryWrapper>
                                                    <ErrorBoundaryWrapper identifier="AirlineFilterWrapperTopBar-desktop-srp">
                                                        <AirlineFilterWrapperTopBar deviceType={DESKTOP} />
                                                    </ErrorBoundaryWrapper>
                                                    <ErrorBoundaryWrapper identifier="FlightResultsWrapper-desktop-srp">
                                                        <FlightResultsWrapper
                                                            onBookFlight={handleFlightSelection}
                                                            onSortByPrice={() => { }}
                                                            onViewDetails={flight => onViewFlightDetails([flight])}
                                                            isBookingInProgress={isBookingInProgress}
                                                            loadingFlightKey={loadingFlightKey}
                                                        />
                                                    </ErrorBoundaryWrapper>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>

                                <ErrorBoundaryWrapper identifier="srp-floating-ai-chat-Desktop">
                                    {srpSmartFilterSort && srpFloatingAIChatEnabled && <SRPFloatingAIChat key={`srp-floating-ai-chat-${selectedJourneyLeg}`} />}
                                </ErrorBoundaryWrapper>



                            </Box>
                        </Box>

                        {/* Flight Details Slider  for one way*/}

                        <FlightDetailsWrapperDesktop
                            open={!!selectedFlightForFlightDetails}
                            onClose={handleFlightDetailsClose}
                        >
                            {!!selectedFlightForFlightDetails ? (
                                <FlightDetailsSlider
                                    onClose={handleFlightDetailsClose}
                                    flights={selectedFlightForFlightDetails}
                                    jsons={jsons!}
                                    sectors={sectors!}
                                    onSelect={() => handleFlightSelection(selectedFlightForFlightDetails[0])}
                                    sid={sid!}
                                    showBaggage={true}
                                    isRtSolutionPair={false}
                                    isBookingInProgress={isBookingInProgress}
                                />
                            ) : null}
                        </FlightDetailsWrapperDesktop>

                        <NearbyAirportPopup
                            open={isOpen}
                            onClose={handleNearbyPopupClose}
                            onContinue={handleNearbyPopupContinue}
                            data={popupData}
                        />

                        <Dialog
                            dir={locale === 'ar' ? 'rtl' : 'ltr'}
                            open={!!selectedFlightForFareFamily}
                            onClose={() => setSelectedFlightForFareFamily(null)}
                            maxWidth="xl"
                            fullWidth
                            slotProps={{
                                paper: { sx: { maxWidth: '80rem', margin: 0, minWidth: 'min(100%,1024px)' } },
                                backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }
                            }}
                            keepMounted
                        >
                            <FareFamilyWrapper
                                flight={selectedFlightForFareFamily ?? undefined}
                                sectors={sectors!}
                                handleClose={handleCloseFareFamily}
                                handleCreateBooking={(flight, fareFamilyData) =>
                                    handleCreateBooking([flight], [fareFamilyData])
                                }
                                jsons={jsons!}
                                desktop={true}
                                rawFareFamilyData={rawFareFamilyData ? rawFareFamilyData : undefined}
                            />
                        </Dialog>
                    </>
                )}
            </Container>


            {showStillAroundPopup && <StillAroundPopup handleReFetchWithSameQuery={handleReFetchWithSameQuery} />}

            <MulticityRoundTripDialog
                open={roundTripDialogOpen}
                onClose={() => setRoundTripDialogOpen(false)}
                onEdit={() => {
                    handleOverlayOpen()
                    openMulticityEditorRef.current()
                }}
            />
        </Box>
    )
}

export default SrpContainerDesktop
